import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { API_ENDPOINTS } from 'src/app/config/api-endpoints';
import { ApplicationContext, SourceSheet, TableConfig } from 'src/app/model';
import { environment } from 'src/environments/environment';
import { ApplicationInsightsService } from '../application-insights/application-insights.service';
import { HttpService } from '../http-service/http.service';
import { ExcelDataMapperService } from './excel-data-mapper.service';
import { WOKRPAPER_SHEETS } from 'src/app/constants/workbook.constants';
import { SaveHeadersProvider } from './save-headers-provider.interface';

@Injectable({
  providedIn: 'root'
})
export class TaxColNotRegisteredDataService implements SaveHeadersProvider {
  private isConfigLoaded = false;
  private config: TableConfig = {
    sheetType: SourceSheet.TaxColNotRegistered,
    columnMappings: [],
  };
  private readonly entityColumnMappings = [
    { Key: 'State', DisplayName: 'State', Column: 'A' },
    { Key: 'EntityName', DisplayName: 'Entity', Column: 'B' },
    { Key: 'VertexCompanyCode', DisplayName: 'Vertex Company Code', Column: 'C' },
    { Key: 'Jurisdiction', DisplayName: 'Jurisdiction', Column: 'D' },
    { Key: 'Notes', DisplayName: 'Notes', Column: 'E' },
    { Key: 'CurrentMonth', DisplayName: 'Current Month', Column: 'G' },
    { Key: 'PriorYears', DisplayName: 'Prior Years', Column: 'I' },
    { Key: 'Jan', DisplayName: 'JAN', Column: 'K' },
    { Key: 'Feb', DisplayName: 'FEB', Column: 'L' },
    { Key: 'Mar', DisplayName: 'MAR', Column: 'M' },
    { Key: 'Apr', DisplayName: 'APR', Column: 'N' },
    { Key: 'May', DisplayName: 'MAY', Column: 'O' },
    { Key: 'Jun', DisplayName: 'JUN', Column: 'P' },
    { Key: 'Jul', DisplayName: 'JUL', Column: 'Q' },
    { Key: 'Aug', DisplayName: 'AUG', Column: 'R' },
    { Key: 'Sep', DisplayName: 'SEP', Column: 'S' },
    { Key: 'Oct', DisplayName: 'OCT', Column: 'T' },
    { Key: 'Nov', DisplayName: 'NOV', Column: 'U' },
    { Key: 'Dec', DisplayName: 'DEC', Column: 'V' },
  ];

  constructor(
    private excelDataMapperService: ExcelDataMapperService,
    private appInsights: ApplicationInsightsService,
    private http: HttpService
  ) {}

  private loadConfig() {
    this.appInsights.trackEvent('TaxColNotRegisteredDataService.loadConfig.called');
    try {
      if (!this.isConfigLoaded) {
        this.appInsights.trackEvent('TaxColNotRegisteredDataService.loadConfig.settingSheetName', { sheetName: 'Tax Col Not Registered' });
        this.appInsights.trackEvent('TaxColNotRegisteredDataService.loadConfig.settingTableName', { tableName: 'TCNRData' });
        this.appInsights.trackEvent('TaxColNotRegisteredDataService.loadConfig.settingColumnMappings');
        this.config.columnMappings = [...this.entityColumnMappings];
        this.isConfigLoaded = true;
        this.appInsights.trackEvent('TaxColNotRegisteredDataService.loadConfig.configLoaded');
      }
    } catch (error: any) {
      this.appInsights.trackException(error);
      this.appInsights.trackException(error, { location: 'TaxColNotRegisteredDataService.loadConfig' });
      this.appInsights.trackEvent('TaxColNotRegisteredDataService.loadConfig.error', { error: error?.message || error });
      console.error('Error loading config in TaxColNotRegisteredDataService:', error);
    }
  }

  public getConfig(): TableConfig {
    this.loadConfig();
    return this.config;
  }

  public getData(appContext: ApplicationContext): Observable<any[]> {
    const isRG = !!appContext.reportingGroupId;
    const endpoint = isRG
      ? API_ENDPOINTS.inputSheet.rgTaxColNotRegistered
      : API_ENDPOINTS.inputSheet.taxColNotRegistered;
    const url = `${environment.apiBaseURL}${endpoint}`;
    return this.http.post(url, appContext);
  }

  public getSaveHeaders(): string[] {
    return Object.keys(this.mapTaxColNotRegistered);
  }

  public prepareTaxColNotRegisteredSheetData(matrix: string[][]): {
    headers: string[];
    rows: (string | number | boolean)[][];
  } {
    const { sqlHeaders, tableRows } = this.excelDataMapperService.buildFilteredData(
      matrix,
      this.mapTaxColNotRegistered,
      WOKRPAPER_SHEETS.TaxColNotregisteredSheet,
      {
        skipRows: 0,
        removeLastRow: true,
        skipRowsWhenAllMappedColumnsBlank: true,
        ignoreBlankCheckColumns: ['Vertex Company Code']
      }
    );

    const notesIndex = sqlHeaders.indexOf('Notes');
    if (notesIndex >= 0) {
      tableRows.forEach((row) => {
        const value = row[notesIndex];
        if (typeof value === 'string' && value.trim() === '') {
          row[notesIndex] = '';
        }
      });
    }

    tableRows.unshift(sqlHeaders);

    return { headers: sqlHeaders, rows: tableRows };
  }

  private mapTaxColNotRegistered: Record<string, { sqlCol: string; dataType: string; required?: boolean }> = {
    'State': { sqlCol: 'State', dataType: 'string', required: true },
    'Vertex Company Code': { sqlCol: 'VertexCompanyCode', dataType: 'string', required: true },
    'Jurisdiction': { sqlCol: 'Jurisdiction', dataType: 'string', required: true },
    'State Abbreviation': { sqlCol: 'StateAbbreviation', dataType: 'string', required: true },
    'Notes': { sqlCol: 'Notes', dataType: 'string', required: false },
    'Current Month': { sqlCol: 'CurrentMonth', dataType: 'number', required: false },
    'Prior Years': { sqlCol: 'PriorYears', dataType: 'number', required: false },
    'Total': { sqlCol: 'Total', dataType: 'number', required: false },
    'Jan': { sqlCol: 'Jan', dataType: 'number', required: false },
    'Feb': { sqlCol: 'Feb', dataType: 'number', required: false },
    'Mar': { sqlCol: 'Mar', dataType: 'number', required: false },
    'Apr': { sqlCol: 'Apr', dataType: 'number', required: false },
    'May': { sqlCol: 'May', dataType: 'number', required: false },
    'Jun': { sqlCol: 'Jun', dataType: 'number', required: false },
    'Jul': { sqlCol: 'Jul', dataType: 'number', required: false },
    'Aug': { sqlCol: 'Aug', dataType: 'number', required: false },
    'Sep': { sqlCol: 'Sep', dataType: 'number', required: false },
    'Oct': { sqlCol: 'Oct', dataType: 'number', required: false },
    'Nov': { sqlCol: 'Nov', dataType: 'number', required: false },
    'Dec': { sqlCol: 'Dec', dataType: 'number', required: false },
  };
}
